import { Link } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"
import { Markdown } from "@holigral/shared/ui"
import { H1, H2 } from "@holigral/shared/ui/components"
import { Badge } from "@holigral/shared/ui/components/ui/badge"
import StatusBadge from "./StatusBadge"
import { AssignmentStatus } from "./model"
import { useGetAssignmentQuery } from "./api"
import { useT } from "@/i18n"
import { Matrix } from "@/matrix"
import { useCurrentTenant } from "@/tenant"
import { LoadingScreen } from "@/ui"
import { formatDate } from "@/utils"
import type { Uuid } from "@/utils"

type Props = {
  readonly assignmentId: Uuid
}

export default function EvaluationPage({ assignmentId }: Props) {
  const { t } = useT("assignment")
  const { tenantSlug } = useCurrentTenant()
  const { data: detail, isLoading } = useGetAssignmentQuery(assignmentId)

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!detail) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 py-8">
        <p className="text-muted-foreground">{t("evaluations.notFound")}</p>
      </div>
    )
  }

  const { assignment, matrix, evaluation } = detail
  const isEvaluated =
    assignment.status === AssignmentStatus.Evaluated && !!matrix && !!evaluation

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-6 py-8">
      <Link
        to="/t/$tenantSlug/evaluations"
        params={{ tenantSlug }}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t("evaluations.back")}
      </Link>
      <div className="flex flex-wrap items-center gap-3">
        <H1>{assignment.respondent.email ?? "—"}</H1>
        <StatusBadge viewer="evaluator" status={assignment.status} />
        {assignment.isSelf && (
          <Badge variant="tag">{t("detail.selfAssignment")}</Badge>
        )}
      </div>
      <dl className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2 text-sm">
        <dt className="text-muted-foreground">{t("detail.requester")}</dt>
        <dd>{assignment.createdBy?.email ?? "—"}</dd>
        <dt className="text-muted-foreground">{t("detail.brief")}</dt>
        <dd>
          {assignment.brief ? (
            <Link
              to="/t/$tenantSlug/briefs/$briefId"
              params={{ tenantSlug, briefId: assignment.brief.id }}
              className="underline underline-offset-3 hover:text-foreground"
            >
              {assignment.brief.name}
            </Link>
          ) : (
            "—"
          )}
        </dd>
        <dt className="text-muted-foreground">{t("detail.sent")}</dt>
        <dd>{formatDate("date", assignment.createdAt)}</dd>
        <dt className="text-muted-foreground">{t("detail.submitted")}</dt>
        <dd>
          {assignment.submittedAt
            ? formatDate("date", assignment.submittedAt)
            : "—"}
        </dd>
        {evaluation?.publishedAt != null && (
          <>
            <dt className="text-muted-foreground">
              {t("evaluations.publishedAt")}
            </dt>
            <dd>{formatDate("date", evaluation.publishedAt)}</dd>
          </>
        )}
      </dl>
      {isEvaluated ? (
        <>
          <section className="flex flex-col gap-3">
            <H2>{t("evaluations.matrix")}</H2>
            <Matrix matrix={matrix} />
          </section>
          <section data-reading className="flex flex-col gap-3">
            <H2>{t("evaluations.evaluation")}</H2>
            <Markdown>{evaluation.markdown}</Markdown>
          </section>
        </>
      ) : (
        <p className="text-muted-foreground">{t("evaluations.notEvaluated")}</p>
      )}
    </div>
  )
}
